import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { UrlTree } from '@angular/router';
import {
	BasicFilterExpression,
	CijferMeasure,
	DataOptions,
	DataResponse,
	DataService,
	ExportDataOptions,
	ExportFilter,
	FilterExpression,
	InFilterExpression,
} from '../../services/data.service';
import { FilterConfigService } from '../../services/filter-config';
import { Observable } from 'rxjs';
import { noAgg, noAgg0, SingleAggregator, sumOver } from '../../services/aggregation';
import { Att, AttrPath, Cijferkolomtype, Table } from '@cumlaude/metadata';
import { ColumnDef, createColumnDef } from '../../shared/components/table/table/table.model';
import { att, percOfRow } from '../../services/measures';
import {
	DataRow,
	DataTreeTableComponent,
	DUMMY_ROW_ID,
	getInitialAttributes,
	getSubgroupAttributes,
} from '../../shared/dashboard/data-tree-table/data-tree-table';
import { getLeafA, Level, Path } from '../../services/data-tree';
import { intersection, last, partition, range, sortBy, sum, union } from 'lodash-es';
import { PivotSeriesHeaderCellComponent } from '../../shared/components/table/cells/pivot-series-header-cell/pivot-series-header-cell.component';
import { ToetsData, ToetsPeriodeCellComponent } from '../../shared/components/table/cells/toets-periode-cell/toets-periode-cell.component';
import { Option, SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { deelVeilig } from '@cumlaude/shared-utils';
import { PivotTableConfig } from '../../shared/dashboard/pivot-table/pivot-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { CijferkolomKeuze, DashboardVariant, Dossier } from '../../services/weergave-opties';
import { InstellingBron } from '@cumlaude/service-contract';
import { PivotTableComponent } from '../../shared/dashboard/pivot-table/pivot-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';

export interface ToetsI extends Attributes {
	cf_fun_nm_kolomgroep: any;
	cf_m_co_kolom_agg: any;
	clc_m_abb_label_avg: any;
	clc_m_is_voldoende_avg: number;
	cf_m_nm_bijzonderheid: any;
	cf_m_abb_kolomkop_agg: any;
	cf_m_des_kolom_agg: any;
	cf_m_nr_leerlingen: number;
	cf_m_nr_cijfer: number;
	cf_m_nr_decimalen: number;
	cf_m_is_gevuld: number;
	cf_m_is_voldoende: number;
	cf_m_nr_weging_agg: any;
	cf_m_nr_tekortpunten: number;
	cf_nm_kolomtype: any;
}

export interface ToetsA extends Attributes {
	cf_nr_leerlingen: number;
	gem_cijfer: number | null;
	sum_cijfer: number | null;
	sum_tekortpunten: number | null;
	cf_is_gevuld: number;
	onvoldoendes: number;
}

@Component({
	selector: 'app-toets',
	templateUrl: './toets.component.html',
	styleUrls: ['./toets.component.scss'],
	providers: [{ provide: DataTreeTableComponent, useExisting: ToetsComponent }], // zorgt dat dit component als subclass van DataTreeTableComponent wordt herkend voor het ViewChild-attribuut van DataTreeTableConfigConfig
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		PivotTableComponent,
		FilterBarComponent,
		SingleSelectDropdownComponent,
		LegendaComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToetsComponent extends PivotTableConfig<ToetsI, ToetsA> {
	protected readonly dataService = inject(DataService);
	protected readonly filterConfigService = inject(FilterConfigService);

	InstellingBron = InstellingBron;

	override groupsAsTitles = [0];

	/**
	 * Het dashboard bestaat uit meerdere tables, 1 voor elke leerfase (= niveau + leerjaar).
	 * Dit omdat de kolommen nogal kunnen verschillen tussen leerfases.
	 */
	tableGroups: AttrPath[] = [['cf_fk_lb', 'lb_nm_leerfase']];

	/**
	 * Subgroups staan vast en zorgen ervoor dat een cel overeenkomt met een bepaalde toets, nl:
	 * 1. van de gekozen groepering
	 * 2. en de periode uit de bovenste kop
	 * 3. en de "cf_fun_nm_kolomgroep" (= ofwel cf_nr_kolom, ofwel cf_abb_kolomkop, afhankelijk van kolomtype)
	 */
	subgroups: AttrPath[] = [['cf_fun_nm_periode'], ['cf_nm_kolomtype'], ['cf_fun_nm_kolomgroep']];

	override availableGroups: AttrPath[] = [
		['cf_fk_ll', 'll_nm_basisschooladvies'],
		['cf_fks_mw', 'mw_nm_medewerker'],
		['cf_nm_opleiding'],
		['cf_nm_klas'],
		['cf_nm_leerling'],
		['cf_nm_lesgroep'],
		['cf_nm_lesgroep_docenten'],
		['cf_nr_leerjaar'],
		['cf_fk_ilt', 'ilt_nm_niveau'],
		['cf_fk_ilt_vorig_sj', 'ilt_nm_niveau'],
		['cf_fk_ilt', 'ilt_abb_profiel'],
		['cf_nm_vak'],
		['cf_nm_vak_uni'],
		['cf_fk_vk', 'vk_nm_vak'],
		['cf_abb_onderwijssoort_vak'],
		['cf_nm_vestiging'],
		['cf_fk_lb_vorig_sj', 'lb_nm_vestiging'],
		['cf_fk_lb_vorig_sj', 'lb_nm_leerfase'],
	];

	toetsFilters: FilterName[] = [
		'cf_nm_schooljaar',
		'cf_co_brin',
		'cf_nm_vestiging',
		'cf_fk_ilt.ilt_nm_niveau',
		'cf_nr_leerjaar',
		'cf_is_alternatievenormering',
		'cf_nm_vak',
		'cf_nm_lesgroep',
		'cf_fun_nm_periode',
		'cf_fk_lb.lb_nm_leerfase',
		'cf_fks_mw.mw_nm_medewerker',
	];

	dossier = this.qp.signal('dossier');

	dossierOpties = Object.values(Dossier).map((val) => new Option(val));

	cijferkolomKeuze = this.qp.signal('cijfertype');

	cijferkolomKeuzes = computed(() => {
		const groups = this.groeperingService.huidigeGroeperingen();
		const options = [CijferkolomKeuze.ALLE, CijferkolomKeuze.TOETS];

		if (groups?.map((group) => group.join('.')).includes('cf_nm_leerling')) options.push(CijferkolomKeuze.ADVIES);

		options.push(CijferkolomKeuze.GEMIDDELDE);

		return options;
	});

	cijferkolomKeuzeOpties = computed(() => this.cijferkolomKeuzes().map((val) => new Option(val)));

	permanentFilters = computed(() => {
		const filters: FilterExpression[] = [];
		if (this.userService.bron() === InstellingBron.Somtoday && this.cijferkolomKeuze() !== CijferkolomKeuze.GEMIDDELDE) {
			filters.push(
				this.dossier() === Dossier.VOORTGANG
					? new BasicFilterExpression(['cf_is_voortgangsdossier'], 1)
					: new BasicFilterExpression(['cf_is_examendossier'], 1)
			);
		}
		const groups = this.groeperingService.huidigeGroeperingen();
		switch (this.cijferkolomKeuze()) {
			case CijferkolomKeuze.ALLE:
				if (!groups.map((group) => group.join('.')).includes('cf_nm_leerling'))
					filters.push(new BasicFilterExpression(['cf_nm_kolomtype'], Cijferkolomtype.ADVIES, '<>'));
				break;
			case CijferkolomKeuze.TOETS:
				filters.push(new BasicFilterExpression(['cf_nm_kolomtype'], Cijferkolomtype.TOETS, '='));
				break;
			case CijferkolomKeuze.GEMIDDELDE:
				filters.push(
					new InFilterExpression(
						['cf_nm_kolomtype'],
						[
							Cijferkolomtype.PERIODEGEMIDDELDE,
							Cijferkolomtype.GEMIDDELDE,
							Cijferkolomtype.RAPPORTCIJFER,
							Cijferkolomtype.SE_CIJFER,
							Cijferkolomtype.CE_CIJFER,
							Cijferkolomtype.EINDCIJFER,
						].filter((value) => this.filterConfigService.cijferKolomTypeAllowed(value))
					)
				);
				break;
			case CijferkolomKeuze.ADVIES:
				filters.push(new BasicFilterExpression(['cf_nm_kolomtype'], Cijferkolomtype.ADVIES, '='));
				break;
			default:
		}
		return filters;
	});

	constructor() {
		super();

		effect(() => {
			const keuzes = this.cijferkolomKeuzes();
			if (this.cijferkolomKeuze() !== CijferkolomKeuze.ADVIES || keuzes.includes(CijferkolomKeuze.ADVIES)) return;

			this.qp.dispatch('cijfertype', CijferkolomKeuze.TOETS);
		});
	}

	override createPivotColumns(
		columnRoot: Level<ToetsA, number[]>,
		context: DashboardContext<ToetsI, ToetsA, ToetsComponent>
	): ColumnDef<DataRow<ToetsA>>[] {
		return columnRoot.c.map((periodeLvl) => this.createPeriodeColumn(periodeLvl, context));
	}

	protected createPeriodeColumn(
		lvl: Level<ToetsA, number[]>,
		context: DashboardContext<ToetsI, ToetsA, ToetsComponent>
	): ColumnDef<DataRow<ToetsA>> {
		const { subgroupNames } = context;
		const periode = lvl.k!;
		const toetsen = lvl.r.map((path) => getSubgroupAttributes<ToetsI>(subgroupNames, path).cf_fun_nm_kolomgroep);
		const colKey = `periode-${periode}-${toetsen.join('-')}`;
		const coldef = createColumnDef<DataRow<ToetsA>>(colKey);
		coldef.sortable = this.cijferkolomKeuze() === CijferkolomKeuze.GEMIDDELDE;
		coldef.header.component = PivotSeriesHeaderCellComponent;
		coldef.header.class = 'toets-cell';
		coldef.header.getValue = () => ({ seriesKey: periode, colKeys: toetsen });
		const start = last(lvl.r[0])!.i;
		const end = last(last(lvl.r))!.i + 1;
		coldef.body.component = ToetsPeriodeCellComponent;
		coldef.body.class = 'toets-cell';
		coldef.body.getValue = this.makePeriodeData(periode, start, end, context);
		return coldef;
	}

	private makePeriodeData(
		periode: string,
		start: number,
		end: number,
		context: DashboardContext<ToetsI, ToetsA, ToetsComponent>
	): (row: DataRow<ToetsA>) => (ToetsData | null)[] | number {
		const { groupNames, subgroupNames, measureNames } = context;
		return (row: DataRow<ToetsA>) => {
			const periodeLvl = last(row._path)!.c.find((lvl) => lvl.k === periode);
			if (row._id === DUMMY_ROW_ID) {
				// wordt aangeroepen bij sorteren
				// sorteren heeft hier alleen zin op rijniveau en bij aanwezige periode
				if (row._path.length !== groupNames.length + 1 || !periodeLvl) return 0;

				return avgCijfer([...row._path, periodeLvl]);
			}
			if (!periodeLvl) return [];

			return fill(
				periodeLvl.r,
				start,
				end,
				(path) => {
					const attrs = getInitialAttributes<ToetsI>(subgroupNames, measureNames, path);
					return this.makeToetsData(attrs, path, context);
				},
				() => null
			);
		};
	}

	protected makeToetsData(attrs: ToetsI, path: Path<ToetsA, number[]>, _context: DashboardContext<ToetsI, ToetsA, ToetsComponent>): ToetsData {
		const perc_onvoldoende = percOfRow('onvoldoendes', 'cf_is_gevuld')(path);
		const {
			cf_m_nr_cijfer,
			cf_m_nr_decimalen,
			clc_m_abb_label_avg,
			clc_m_is_voldoende_avg,
			cf_m_abb_kolomkop_agg,
			cf_m_co_kolom_agg,
			cf_m_des_kolom_agg,
			cf_m_nr_leerlingen,
			cf_m_nr_weging_agg,
			cf_m_nm_bijzonderheid,
			cf_nm_kolomtype,
			cf_m_is_voldoende,
		} = attrs;
		return {
			cf_m_nr_cijfer,
			cf_m_nr_decimalen,
			clc_m_abb_label_avg,
			clc_m_is_voldoende_avg,
			cf_m_nm_bijzonderheid,
			cf_nm_kolomtype,
			cf_m_is_voldoende,
			abb_kolomkop: cf_m_abb_kolomkop_agg,
			co_kolom: cf_m_co_kolom_agg,
			des_kolom: cf_m_des_kolom_agg,
			nr_leerlingen: cf_m_nr_leerlingen,
			nr_weging: cf_m_nr_weging_agg,
			perc_onvoldoende,
		};
	}

	override createMeasureColumns(context: DashboardContext<ToetsI, ToetsA, ToetsComponent>): ColumnDef<DataRow<ToetsA>>[] {
		const lln = this.createLeerlingColumn('lln', att('cf_nr_leerlingen'), { context });
		lln.header.class = 'lln-header';
		return [lln];
	}

	override getSelectionConfig(context: DataRequestContext<ToetsA>): SelectionConfig<ToetsA> | undefined {
		return { ...super.getSelectionConfig(context)!, getSize: att('cf_nr_leerlingen') };
	}

	override getRowClickUrl(context: DashboardContext<ToetsI, ToetsA, ToetsComponent>, path: Path<ToetsA, number[]>): UrlTree | undefined {
		const atts: Att[] = ['cf_nm_vak', 'cf_nm_lesgroep', 'cf_nm_leerling'];
		return this.getZoomClickUrl(atts, context.groupNames, path, { ignoreFirst: 1 }) ?? super.getRowClickUrl(context, path);
	}

	factTable = Table.fac_cf_cijfer;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getCijfersData({
			...options,
			m: [CijferMeasure.STATS, CijferMeasure.LABEL, CijferMeasure.BIJZONDERHEID, CijferMeasure.TOETS],
			r: [0, this.tableGroups.length, this.groeperingService.huidigeGroeperingen().length + this.tableGroups.length],
		});
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getCijfersExportData(options);
	}

	getSelectedDossierOptie() {
		return this.dossierOpties.find((optie) => optie.value === this.dossier())!;
	}

	getSelectedCijfertypeOptie() {
		return this.cijferkolomKeuzeOpties().find((optie) => optie.value === this.cijferkolomKeuze());
	}

	override getDisplayOptions(): ExportFilter[] {
		return [
			...(this.userService.bron() === InstellingBron.Somtoday ? [{ label: 'Toetsdossier', value: this.dossier() }] : []),
			{ label: 'Cijfertype', value: this.cijferkolomKeuze() === CijferkolomKeuze.ALLE ? 'Alle' : this.cijferkolomKeuze() },
		];
	}

	protected override singleAggregators: Partial<{ [ai in keyof ToetsA]: SingleAggregator<ToetsI, ToetsA[ai]> }> = {
		cf_nr_leerlingen: noAgg0('cf_m_nr_leerlingen'),
		gem_cijfer: noAgg('cf_m_nr_cijfer'),
		sum_cijfer: {
			init: ({ cf_m_nr_cijfer, cf_m_is_gevuld }) => cf_m_nr_cijfer * cf_m_is_gevuld,
			combine: (as) => sum(as),
		},
		cf_is_gevuld: sumOver('cf_m_is_gevuld'),
		onvoldoendes: {
			init: ({ cf_m_is_voldoende, cf_m_is_gevuld }) => cf_m_is_gevuld - cf_m_is_voldoende,
			combine: (as) => sum(as),
		},
	};

	public sortToetsKolommen(colPath: (string | null)[], keys0: (string | null)[], keys1: (string | null)[]): (string | null)[] {
		const allKeys = union(keys0, keys1);

		//cf_nm_kolomtype
		if (colPath.length == 2) {
			return intersection(
				[
					Cijferkolomtype.TOETS,
					Cijferkolomtype.ADVIES,
					Cijferkolomtype.PERIODEGEMIDDELDE,
					Cijferkolomtype.GEMIDDELDE,
					Cijferkolomtype.RAPPORTCIJFER,
					Cijferkolomtype.SE_CIJFER,
					Cijferkolomtype.CE_CIJFER,
					Cijferkolomtype.EINDCIJFER,
				],
				allKeys
			);
		}

		const [otherKeys, numberKeys] = partition(allKeys, (k) => Number.isNaN(Number(k)));
		return [...sortBy(numberKeys, [Number]), ...sortBy(otherKeys)];
	}

	protected readonly DashboardVariant = DashboardVariant;
	protected readonly CijferkolomKeuze = CijferkolomKeuze;
}

// alleen bedoeld om te sorteren
function avgCijfer(path: Path<ToetsA, unknown>) {
	const { sum_cijfer, cf_is_gevuld } = getLeafA(path);
	return deelVeilig(sum_cijfer ?? 0, cf_is_gevuld);
}

/**
 * Vult een gehele rij, op basis van gedeeltelijk aanwezige records. Niet-aanwezige records krijgen een placeholder.
 *
 * @param paths De records, met op hun laagste niveau in het "i" veld de kolom-positie.
 * @param start "i" index voor de eerste kolom.
 * @param end "i" index voor de laatste kolom + 1
 * @param mapVal produceert een waarde voor een bestaand record
 * @param fillVal produceert een placeholder voor een niet-bestaand record
 */
function fill<A, D, T>(paths: Path<A, D>[], start: number, end: number, mapVal: (path: Path<A, D>) => T, fillVal: (ix: number) => T): T[] {
	const present: { [n: number]: T } = {};
	for (const path of paths) {
		present[last(path)!.i] = mapVal(path);
	}
	return range(start, end).map((i) => present[i] ?? fillVal(i));
}
